import { ResponseNotOkError } from "@/api"
import { aiSearchMakeGetRequest } from "app/static/frontend/search-new/action-creators"
import {
    checkDoesNotHaveAnyRegion,
    checkHasAllPermissions,
    checkHasRegion,
    permissionFilter,
} from "shared/imports/permissionFunctions"
import { internationalWithoutBillsRegions } from "shared/imports/regionConstants"
import { sortBySearchOrder } from "shared/imports/sharedHelperFunctions"
import { kseFilter, msaDatasetFilter, ymcaDatasetFilter } from "shared/search/helperFunctions"
import isFeatureEnabled from "shared/featureflags/helperFunctions"

const { QuorumDataType } = DjangIO.app.models
const allowedQdts = [QuorumDataType.person.value, QuorumDataType.note.value]
export const UNSUPPORTED_DATASET =
    "Only the Notes and Officials datasets are supported at this time. If you believe this was a mistake, " +
    "please try again, and include the name of the dataset you wish to search."

/**
 * Submits user's query to the Magic Search service, and returns an initialFilter object. This is different from
 * the non-mobile version, because we need to check a list of allowed datasets that are ready to displayed on the PWA.
 */
export const generateAiSearchFiltersForMobile = async (query: string) => {
    // make a call to backend API using action creators
    // if we get a response back, construct the new search URL and segue to it
    // if we dont get a response, change aiSearchStatus to "not found" and throw error
    const response = await aiSearchMakeGetRequest(query)

    if (!Object.keys(response).length) {
        throw new ResponseNotOkError(response)
    }

    // set the QDT based on the response's dataset value
    // dataset value will be plural, remove the (s) at the end of string
    // since official dataset is on Person model, need to statically set it
    const qdtString =
        response.dataset && response.dataset[response.dataset.length - 1] === "s"
            ? response.dataset.slice(0, response.dataset.length - 1)
            : response.dataset
    const aiSearchQdt =
        qdtString === "official"
            ? DjangIO.app.models.QuorumDataType.person.value
            : DjangIO.app.models.QuorumDataType[qdtString].value

    if (allowedQdts.includes(aiSearchQdt)) {
        // initialize filter object with ai_search user query, then iterate through filter array and apply each filter to single object
        const initialFilter = response.filters.reduce(
            (acc: { ai_search: any }, filter: any) => ({
                ...acc,
                ...filter,
            }),
            { ai_search: response.question },
        )

        return { initialFilter, aiSearchQdt }
    }

    return { errorMessage: UNSUPPORTED_DATASET }
}

// loosely based on shared/search/search-page-options.js > getCurrentRegionSearchOptions
const SEARCH_LINKS = [
    ...(checkHasAllPermissions([DjangIO.app.models.PermissionType.legislative_data])
        ? [
              {
                  label: DjangIO.app.models.QuorumDataType.bill.plural,
                  icon: "file-alt",
                  quorumDataType: DjangIO.app.models.QuorumDataType.bill,
                  excludedByRegions: checkHasRegion([DjangIO.app.models.Region.global_region.value])
                      ? []
                      : internationalWithoutBillsRegions,
              },
          ]
        : []),
    {
        label: "Officials",
        icon: "users",
        quorumDataType: DjangIO.app.models.QuorumDataType.person,
    },
    {
        label: "Contacts",
        icon: "user-circle",
        quorumDataType: DjangIO.app.models.QuorumDataType.supporter,
    },
    {
        label: "Notes",
        icon: "pencil-alt",
        quorumDataType: DjangIO.app.models.QuorumDataType.note,
    },
    {
        label: "Staffers",
        icon: "address-card",
        quorumDataType: DjangIO.app.models.QuorumDataType.staffer,
    },
    {
        label: "Alerts",
        icon: "rss",
        quorumDataType: DjangIO.app.models.QuorumDataType.alert,
    },
]

export const getSearchLinks = () =>
    SEARCH_LINKS.filter(kseFilter)
        .filter(msaDatasetFilter)
        .filter(ymcaDatasetFilter)
        .filter(
            (item) =>
                !isFeatureEnabled("ff_pwa_homepage") || item.quorumDataType !== DjangIO.app.models.QuorumDataType.alert,
        )
        .filter((item) =>
            permissionFilter(item.quorumDataType, {
                regions: item.quorumDataType.supported_regions,
                useCurrentRegions: true,
            }),
        )
        .filter((item) => (item.excludedByRegions ? checkDoesNotHaveAnyRegion(item.excludedByRegions) : true))
        .sort((a, b) => sortBySearchOrder(a.quorumDataType, b.quorumDataType))
